import { useState, useCallback } from 'react';
import {
    parseCategoryPaste,
    parseCategoryCsvText,
    parseSlideTableText,
    readFileAsText,
} from './parseBulk';
import styles from './BulkImportModal.module.css';

/**
 * @param {'categories' | 'slideInfo'} props.mode
 * @param {() => void} props.onClose
 * @param {(
 *   rows: { id: string, label: string }[] | Record<string, string>[],
 *   mergeMode: string
 * ) => void} props.onApply
 * @param {import('react').ReactNode} [props.extraFooterActions] — e.g. template download (slideInfo)
 */
export function BulkImportModal({ mode, onClose, onApply, extraFooterActions = null }) {
    const [tab, setTab] = useState('paste');
    const [pasteText, setPasteText] = useState('');
    const [mergeMode, setMergeMode] = useState(mode === 'slideInfo' ? 'merge' : 'append');
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);

    const title =
        mode === 'categories'
            ? 'Paste or import categories'
            : 'Paste or import slide metadata';

    const parseFromText = useCallback((text) => {
        if (mode === 'categories') {
            return parseCategoryPaste(text);
        }
        return parseSlideTableText(text);
    }, [mode]);

    const runPreview = useCallback(() => {
        setError(null);
        try {
            const rows = parseFromText(pasteText);
            setPreview(rows);
            if (rows.length === 0) {
                setError(
                    mode === 'categories'
                        ? 'No categories parsed. One id per line, optional tab/comma label.'
                        : 'No rows parsed from pasted text.',
                );
            }
        } catch (e) {
            setPreview(null);
            setError(e.message || 'Parse failed.');
        }
    }, [mode, pasteText, parseFromText]);

    const handleFile = useCallback(
        async (e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (!f) return;
            setError(null);
            try {
                const text = await readFileAsText(f);
                if (mode === 'categories') {
                    const rows = parseCategoryCsvText(text);
                    setPreview(rows);
                    if (rows.length === 0) setError('No rows in CSV.');
                } else {
                    const rows = parseSlideTableText(text);
                    setPreview(rows);
                }
            } catch (err) {
                setPreview(null);
                setError(err.message || 'Failed to parse file.');
            }
        },
        [mode],
    );

    function handleApply() {
        setError(null);
        let rows = null;

        if (tab === 'paste') {
            try {
                rows = parseFromText(pasteText);
                setPreview(rows);
            } catch (e) {
                setError(e.message || 'Parse failed.');
                return;
            }
        } else {
            rows = preview;
            if (!rows) {
                setError('Choose a file first.');
                return;
            }
        }

        if (!rows || rows.length === 0) {
            setError(
                mode === 'categories'
                    ? 'No categories parsed. One id per line, optional tab/comma label.'
                    : 'No rows parsed. Check your input and try again.',
            );
            return;
        }

        onApply(rows, mergeMode);
        onClose();
    }

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="bulk-import-title">
            <div className={styles.dialog}>
                <h3 id="bulk-import-title" className={styles.title}>{title}</h3>

                <div className={styles.tabs}>
                    <button
                        type="button"
                        className={tab === 'paste' ? styles.tabActive : styles.tab}
                        onClick={() => setTab('paste')}
                    >
                        Paste text
                    </button>
                    <button
                        type="button"
                        className={tab === 'file' ? styles.tabActive : styles.tab}
                        onClick={() => setTab('file')}
                    >
                        CSV file
                    </button>
                </div>

                {tab === 'paste' && (
                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="bulk-paste">
                            {mode === 'categories'
                                ? 'One category per line: id, or id<TAB>label / id,label'
                                : 'Paste CSV/TSV with header row (include filename column or use first column).'}
                        </label>
                        <textarea
                            id="bulk-paste"
                            className={styles.textarea}
                            rows={10}
                            value={pasteText}
                            onChange={(ev) => {
                                setPasteText(ev.target.value);
                                if (error) setError(null);
                            }}
                        />
                    </div>
                )}

                {tab === 'file' && (
                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="bulk-file">Choose file</label>
                        <input
                            id="bulk-file"
                            type="file"
                            accept=".csv,.txt,text/csv,text/plain"
                            onChange={handleFile}
                        />
                    </div>
                )}

                <div className={styles.field}>
                    <span className={styles.label}>Apply mode</span>
                    {mode === 'categories' ? (
                        <div className={styles.radioRow}>
                            <label>
                                <input
                                    type="radio"
                                    name="merge"
                                    checked={mergeMode === 'replace'}
                                    onChange={() => setMergeMode('replace')}
                                />
                                Replace all categories
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="merge"
                                    checked={mergeMode === 'append'}
                                    onChange={() => setMergeMode('append')}
                                />
                                Append (skip duplicate ids)
                            </label>
                        </div>
                    ) : (
                        <div className={styles.radioRow}>
                            <label>
                                <input
                                    type="radio"
                                    name="merge"
                                    checked={mergeMode === 'replace'}
                                    onChange={() => setMergeMode('replace')}
                                />
                                Replace all slide rows
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="merge"
                                    checked={mergeMode === 'merge'}
                                    onChange={() => setMergeMode('merge')}
                                />
                                Merge by filename
                            </label>
                        </div>
                    )}
                </div>

                {error && <p className={styles.error}>{error}</p>}

                {preview && preview.length > 0 && (
                    <div className={styles.preview}>
                        <div className={styles.previewTitle}>
                            Preview ({preview.length} row{preview.length === 1 ? '' : 's'})
                        </div>
                        <div className={styles.previewTableWrap}>
                            <table className={styles.previewTable}>
                                <thead>
                                    <tr>
                                        {mode === 'categories' ? (
                                            <>
                                                <th>id</th>
                                                <th>label</th>
                                            </>
                                        ) : (
                                            Object.keys(preview[0]).map((k) => (
                                                <th key={k}>{k}</th>
                                            ))
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.slice(0, 15).map((row, i) => (
                                        <tr key={i}>
                                            {mode === 'categories' ? (
                                                <>
                                                    <td>{row.id}</td>
                                                    <td>{row.label}</td>
                                                </>
                                            ) : (
                                                Object.keys(preview[0]).map((k) => (
                                                    <td key={k}>{row[k]}</td>
                                                ))
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {preview.length > 15 && (
                            <p className={styles.more}>… and {preview.length - 15} more</p>
                        )}
                    </div>
                )}

                <div className={styles.actions}>
                    {extraFooterActions ? (
                        <div className={styles.actionsExtras}>{extraFooterActions}</div>
                    ) : null}
                    <button type="button" className={styles.btnSecondary} onClick={onClose}>
                        Cancel
                    </button>
                    {tab === 'paste' && (
                        <button type="button" className={styles.btnSecondary} onClick={runPreview}>
                            Preview
                        </button>
                    )}
                    <button type="button" className={styles.btnPrimary} onClick={handleApply}>
                        Apply
                    </button>
                </div>
            </div>
        </div>
    );
}
